import { Fragment } from 'react'
import type { CSSProperties, Ref } from 'react'
import type { Category, Product } from '../../types'

/**
 * Menu prices, which are not till prices.
 *
 * The till writes "350,00 ₺" because a receipt has to reconcile to the kuruş.
 * A printed menu is read across a room, and menu prices are whole lira, the
 * ",00" is three characters of noise on every line. Fractional prices, if a
 * yurt ever has one, still print in full.
 */
function menuPrice(kurus: number): string {
  const lira = kurus / 100
  const shown =
    kurus % 100 === 0 ? String(lira) : lira.toFixed(2).replace('.', ',')
  return `${shown}₺`
}

/** The two paper sizes a yurt actually has to hand. */
export type SheetSize = 'a5' | 'a4'

interface Props {
  title: string
  /** The small script word under the title ("Lezzetleri"). */
  tagline?: string
  /** Service hours, printed under the title. Blank prints nothing. */
  hours?: string
  /** Data URI of the logo above the title. Blank prints no logo. */
  logo?: string | null
  /** The script line along the foot. */
  footer?: string
  size: SheetSize
  /** Sorted categories; empty ones are dropped here rather than by the caller. */
  categories: Category[]
  /** Only what the owner ticked, already filtered. */
  products: Product[]
  /** Two columns fits a long menu on one sheet; one is easier to read. */
  columns: 1 | 2
  showPrices: boolean
  showDescriptions: boolean
  /**
   * Shown on screen rather than only to the printer.
   *
   * Off, the sheet is `display: none` and exists solely for the print
   * stylesheet, the arrangement the receipt uses. On, it is drawn at its real
   * millimetre size and scaled by --m-zoom to fit the pane, so what is on the
   * screen breaks its lines exactly where the paper will.
   */
  preview?: boolean
  /** Scale applied in preview; ignored when printing. */
  zoom?: number
  /**
   * Where to sit on screen, in viewport coordinates.
   *
   * The card is portalled to <body> so printing can hide the app outright, so
   * it no longer inherits a position from the preview pane. The dialog
   * measures the empty stage left in the pane and passes its corner here.
   * Ignored on paper, where the card is the whole document.
   */
  spot?: { top: number; left: number; clip?: string } | null
  /**
   * Type scale, so a long menu still lands on one sheet.
   *
   * Applied to the sheet's own font-size, which every other size on the card
   * is expressed in ems of, so one number shrinks headings, prices, rules and
   * the gaps between them together, and the card keeps its proportions instead
   * of collapsing into dense text between unchanged margins.
   */
  fit?: number
  /** Measured by the dialog to work out whether the content overflows. */
  bodyRef?: Ref<HTMLDivElement>
}

/**
 * The printable menu card.
 *
 * Rendered into the layout permanently and shown only to the print stylesheet,
 * the same arrangement the receipt uses, a second window would lose the app's
 * fonts and need the catalogue fetched again.
 *
 * Millimetres throughout rather than pixels: this is the one part of the app
 * whose output is a physical object, and a 4mm gutter is a thing you can check
 * against the paper in your hand.
 */
export function MenuSheet({
  title,
  tagline,
  hours,
  footer,
  size,
  categories,
  products,
  columns,
  showPrices,
  showDescriptions,
  logo,
  preview = false,
  zoom = 1,
  spot,
  fit = 1,
  bodyRef,
}: Props) {
  const grouped = categories
    .map((c) => ({ category: c, items: products.filter((p) => p.categoryId === c.id) }))
    .filter((g) => g.items.length > 0)

  /**
   * Categories dealt into columns by hand rather than by CSS `column-count`.
   *
   * CSS columns turn vertical overflow into horizontal, content past the
   * bottom of the last column silently starts another one off the edge of the
   * page, which is exactly what a fitter measuring height cannot see. Laying
   * the columns out as flex children makes a third column structurally
   * impossible: too much content can only make these two taller, which is
   * overflow the fitter reads reliably.
   *
   * Split by estimated height rather than by count, so a section of twelve
   * items does not sit opposite one of two. A description wraps to roughly a
   * second line, hence the weight.
   */
  const columnsOf = (): (typeof grouped)[] => {
    if (columns === 1) return [grouped]

    const cost = (g: (typeof grouped)[number]) =>
      2.4 + g.items.reduce((n, p) => n + (showDescriptions && p.description ? 2 : 1), 0)

    const total = grouped.reduce((n, g) => n + cost(g), 0)

    // Every split point is tried and the most even wins. A greedy fill,
    // taking sections until the first column passes halfway, cannot see that
    // the section it is about to place is a large one, and left the columns
    // seven lines apart on a real menu. There are only ever a handful of
    // categories, so checking them all costs nothing.
    let best = grouped.length
    let bestGap = Infinity
    let running = 0

    for (let i = 0; i < grouped.length; i++) {
      running += cost(grouped[i])
      const gap = Math.abs(running - (total - running))
      if (gap < bestGap) {
        bestGap = gap
        best = i + 1
      }
    }

    const left = grouped.slice(0, best)
    const right = grouped.slice(best)

    // Everything landing on one side happens when a single section outweighs
    // all the rest; one column of content is better than one empty column.
    return right.length === 0 ? [left] : [left, right]
  }

  const laid = columnsOf()

  return (
    <div
      id="menu-sheet-root"
      className={`msheet msheet--${size} msheet--cols${columns} ${preview ? 'is-preview' : ''}`}
      data-size={size}
      style={
        {
          ...(preview ? { '--m-zoom': zoom } : {}),
          ...(spot ? { top: `${spot.top}px`, left: `${spot.left}px` } : {}),
          ...(spot?.clip ? { clipPath: spot.clip } : {}),
          '--m-fit': fit,
        } as CSSProperties
      }
    >
      <header className="msheet__head">
        {logo && <img className="msheet__logo" src={logo} alt="" />}
        <span className="msheet__title">{title}</span>
        {tagline && <span className="msheet__tagline">{tagline}</span>}
        {hours && (
          <span className="msheet__hours">
            {/* Drawn rather than an emoji: an emoji clock prints as a colour
                blob on some drivers and as a hollow box on others. */}
            <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true">
              <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
              <path
                d="M12 7v5l3.2 2"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            {hours}
          </span>
        )}
      </header>

      <div className="msheet__body" ref={bodyRef}>
        {laid.map((column, i) => (
          <Fragment key={i}>
            {/* Drawn between the columns rather than as a border on one, so it
                runs the full height of the page while the columns stay as tall
                as their content, which is what the fitter measures. */}
            {i > 0 && <div className="msheet__rule" aria-hidden="true" />}
            <div
              className="msheet__col"
              // Marks this as content the fitter should measure; the rule above
              // is stretched and must not be counted. See useFitToPage.ts.
              data-fit-column=""
            >
            {column.map(({ category, items }) => (
              <section key={category.id} className="msheet__cat">
                <h2 className="msheet__cat-name">{category.name}</h2>

                <ul className="msheet__items">
                  {items.map((p) => (
                    <li key={p.id} className="msheet__item">
                      <div className="msheet__line">
                        <span className="msheet__name">{p.name}</span>
                        {/* The dotted run between name and price. A flexible
                            element rather than a text-decoration, so it
                            stretches to whatever gap the name leaves. */}
                        {showPrices && <span className="msheet__dots" aria-hidden="true" />}
                        {showPrices && (
                          <span className="msheet__price">{menuPrice(p.priceKurus)}</span>
                        )}
                      </div>
                      {/* No empty row when an item has no description: the
                          space closes up rather than leaving a gap the eye
                          reads as a missing line. */}
                      {showDescriptions && p.description && (
                        <p className="msheet__desc">({p.description})</p>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            </div>
          </Fragment>
        ))}
      </div>

      {footer && (
        <footer className="msheet__foot">
          <span className="msheet__foot-rule" aria-hidden="true" />
          <span className="msheet__foot-text">{footer}</span>
          <span className="msheet__foot-rule" aria-hidden="true" />
        </footer>
      )}
    </div>
  )
}
